import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import styles from "../assets/propertyNoticeToQuitIndex.module.css";
import Loader from "../components/Loader";

const PropertyNoticeToQuitIndex = () => {
  const navigate = useNavigate();

  const [noticeToQuits, setNoticeToQuits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiFailed, setApiFailed] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState({
    creator: true,
    property: true,
    noticeDate: true,
  });
  const [showColumnVisibility, setShowColumnVisibility] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(30);
  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    loadNoticeToQuits();
  }, []);

  // Close column visibility dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showColumnVisibility &&
        !event.target.closest(`.${styles["column-visibility-container"]}`)
      ) {
        setShowColumnVisibility(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showColumnVisibility]);

  const loadNoticeToQuits = async () => {
    try {
      setLoading(true);
      setApiFailed(false);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/properties/notice-to-quit`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          console.log("API authentication failed");
          setApiFailed(true);
          setNoticeToQuits([]);
          setAllData([]);
          setLoading(false);
          return;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Ensure we have an array
      let noticeToQuitsData = [];

      if (data && typeof data === "object") {
        if (Array.isArray(data)) {
          noticeToQuitsData = data;
        } else if (data.noticeToQuits && Array.isArray(data.noticeToQuits)) {
          noticeToQuitsData = data.noticeToQuits;
        } else if (data.data && Array.isArray(data.data)) {
          noticeToQuitsData = data.data;
        } else if (data.items && Array.isArray(data.items)) {
          noticeToQuitsData = data.items;
        } else if (data.results && Array.isArray(data.results)) {
          noticeToQuitsData = data.results;
        } else {
          console.warn("No valid array found in response:", data);
          console.warn("Available keys:", Object.keys(data));
          noticeToQuitsData = [];
        }
      } else {
        console.warn("Invalid data structure received:", data);
        noticeToQuitsData = [];
      }

      // Remove duplicates based on id (safety measure)
      const uniqueData = noticeToQuitsData.filter((record, index, self) =>
        index === self.findIndex((r) => r.id === record.id)
      );

      if (uniqueData.length !== noticeToQuitsData.length) {
        console.log(
          `⚠️ [FRONTEND] Removed ${noticeToQuitsData.length - uniqueData.length} duplicate records`
        );
      }

      setAllData(uniqueData);
      setNoticeToQuits(uniqueData.slice(0, entriesPerPage));
      setLoading(false);

      // Show success message if data loaded
      // if (noticeToQuitsData.length > 0) {
      //   toast.success(`Loaded ${noticeToQuitsData.length} property notice to quit records`);
      // }
    } catch (err) {
      console.error("Error loading property notice to quit list:", err);
      setError(err.message);
      setApiFailed(true);
      setNoticeToQuits([]);
      setAllData([]);
      setLoading(false);

      // Error handled silently - no toast shown
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      const day = date.getDate().toString().padStart(2, "0");
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, "0");
      const minutes = date.getMinutes().toString().padStart(2, "0");
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch (error) {
      return dateString;
    }
  };

  const handleRowClick = (noticeToQuit) => {
    // Use property_id from backend response for navigation
    const propertyId = noticeToQuit.property_id || noticeToQuit.property?.id;
    if (propertyId) {
      navigate(`/properties/${propertyId}`);
    } else {
      console.error("No property ID found for navigation:", noticeToQuit);
      // Error handled silently - no toast shown
    }
  };

  const handleExportCSV = () => {
    const csvContent =
      "data:text/csv;charset=utf-8," +
      "Creator,Property,Property ID,Address,Postcode,Status,Notice Date\n" +
      noticeToQuits
        .map(
          (item) =>
            `"${item.creator_name || ""}","${
              item.property_name || ""
            }","${item.property_id_display || ""}","${
              item.property_address || ""
            }","${item.property_postcode || ""}","${
              item.property_status || ""
            }","${formatDate(item.date_notice)}"`
        )
        .join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "property_notice_to_quit.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportPDF = async () => {
    try {
      console.log("📄 [FRONTEND] Starting PDF export for Property Notice To Quit...");
      
      // Create PDF content using jsPDF (copying from ExpenseAccount.jsx)
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF();
      
      // Add title
      doc.setFontSize(16);
      doc.text('Property Notice To Quit Report', 20, 20);
      doc.setFontSize(10);
      doc.text(`Generated on: ${new Date().toLocaleDateString('en-GB')}`, 20, 30);
      doc.text(`Total Records: ${noticeToQuits.length}`, 20, 40);
      
      // Add table headers
      const headers = [];
      if (visibleColumns.creator) headers.push('Creator');
      if (visibleColumns.property) headers.push('Property');
      if (visibleColumns.noticeDate) headers.push('Notice Date');
      
      let yPosition = 60;
      const startX = 20;
      const colWidth = 60;
      
      // Draw headers
      headers.forEach((header, index) => {
        doc.text(header, startX + (index * colWidth), yPosition);
      });
      
      yPosition += 10;
      
      // Draw data rows
      noticeToQuits.forEach((item, index) => {
        if (yPosition > 280) {
          doc.addPage();
          yPosition = 20;
        }
        
        const rowData = [];
        if (visibleColumns.creator) rowData.push(String(item.creator_name || 'Unknown').substring(0, 20));
        if (visibleColumns.property) rowData.push(String(item.property_name || 'Unknown Property').substring(0, 20));
        if (visibleColumns.noticeDate) rowData.push(String(formatDate(item.date_notice) || '').substring(0, 20));
        
        rowData.forEach((cell, cellIndex) => {
          const safeCell = String(cell || '');
          doc.text(safeCell.substring(0, 20), startX + (cellIndex * colWidth), yPosition);
        });
        
        yPosition += 7;
      });
      
      // Save PDF
      doc.save(`property_notice_to_quit_${new Date().toISOString().split('T')[0]}.pdf`);
      
      console.log('PDF exported successfully!');
      toast.success("PDF exported successfully!");
    } catch (error) {
      console.error('Export PDF error:', error);
      // Error handled silently - no toast shown
    }
  };

  const handlePrintTable = () => {
    window.print();
  };

  const handleColumnVisibility = () => {
    setShowColumnVisibility(!showColumnVisibility);
  };

  const toggleColumn = (columnName) => {
    setVisibleColumns((prev) => ({
      ...prev,
      [columnName]: !prev[columnName],
    }));
  };

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);

    // If search is empty, show all data
    if (!term.trim()) {
      setFilteredData(allData);
      setNoticeToQuits(allData.slice(0, entriesPerPage));
      setCurrentPage(1);
      return;
    }

    // Filter the current allData - check multiple possible field names
    const filtered = allData.filter((item) => {
      const creatorName = item.creator?.name || item.creator_name || "";
      const propertyName = item.property?.name || item.property_name || "";

      return (
        creatorName.toLowerCase().includes(term) ||
        propertyName.toLowerCase().includes(term)
      );
    });

    setFilteredData(filtered);
    setNoticeToQuits(filtered.slice(0, entriesPerPage));
    setCurrentPage(1);
  };

  const handleEntriesChange = (e) => {
    const entries = parseInt(e.target.value);
    setEntriesPerPage(entries);
    setCurrentPage(1);
    updateDisplayedData(1, entries);
  };

  const updateDisplayedData = (page, perPage) => {
    const startIndex = (page - 1) * perPage;
    const endIndex = startIndex + perPage;
    setNoticeToQuits(allData.slice(startIndex, endIndex));
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    updateDisplayedData(page, entriesPerPage);
  };

  const getPaginationInfo = () => {
    const totalEntries = allData.length;
    const startEntry = (currentPage - 1) * entriesPerPage + 1;
    const endEntry = Math.min(currentPage * entriesPerPage, totalEntries);
    return { startEntry, endEntry, totalEntries };
  };

  const getPaginationButtons = () => {
    const totalPages = Math.ceil(allData.length / entriesPerPage);
    const buttons = [];

    // Previous button
    buttons.push({
      type: "previous",
      disabled: currentPage === 1,
      page: currentPage - 1,
    });

    // Page numbers
    if (totalPages <= 7) {
      // Show all pages if 7 or fewer
      for (let i = 1; i <= totalPages; i++) {
        buttons.push({ type: "number", page: i, active: i === currentPage });
      }
    } else {
      // Show first page
      buttons.push({ type: "number", page: 1, active: currentPage === 1 });

      if (currentPage <= 4) {
        // Show pages 2-5
        for (let i = 2; i <= 5; i++) {
          buttons.push({ type: "number", page: i, active: i === currentPage });
        }
        buttons.push({ type: "ellipsis" });
        buttons.push({ type: "number", page: totalPages, active: false });
      } else if (currentPage >= totalPages - 3) {
        // Show last 5 pages
        buttons.push({ type: "ellipsis" });
        for (let i = totalPages - 4; i <= totalPages; i++) {
          buttons.push({ type: "number", page: i, active: i === currentPage });
        }
      } else {
        // Show current page with surrounding pages
        buttons.push({ type: "ellipsis" });
        for (let i = currentPage - 1; i <= currentPage + 1; i++) {
          buttons.push({ type: "number", page: i, active: i === currentPage });
        }
        buttons.push({ type: "ellipsis" });
        buttons.push({ type: "number", page: totalPages, active: false });
      }
    }

    // Next button
    buttons.push({
      type: "next",
      disabled: currentPage === totalPages,
      page: currentPage + 1,
    });

    return buttons;
  };

  if (loading) return <Loader />;

  return (
    <div className={styles["property-notice-to-quit-index"]}>
      {/* Breadcrumbs */}

      <div className="breadcrumb-row">
        <span
          className="breadcrumb-main"
          style={{ fontWeight: 400, fontSize: "14px", color: "black", cursor: 'pointer' }}
          onClick={() => navigate('/dashboard')}
        >
          Dashboard
        </span>
        <span
          className="breadcrumb-main"
          style={{ fontWeight: 400, fontSize: "14px", color: "black", cursor: 'pointer' }}
          onClick={() => navigate('/properties')}
        >
          Properties
        </span>
        <div
          style={{ background: "#30C693", fontWeight: 400, fontSize: "14px" }}
        >
          <span className="breadcrumb-sep">/</span>
          <span className="breadcrumb-properties">Notice To Quit</span>
        </div>
      </div>

      <div
        className={styles["container-fluid"]}
        style={{ background: "transparent", boxShadow: "none" }}
      >
        <div
          className={styles["row"]}
          style={{ background: "transparent", boxShadow: "none" }}
        >
          <div
            className={styles["col-xs-12"]}
            style={{
              background: "transparent",

              boxShadow: "none",
            }}
          >
            <div
              style={{
                background: "transparent",
                boxShadow: "none",
                border: "none",
              }}
              className={`${styles["panel"]} ${styles["panel-default"]} ${styles["panel-standard"]}`}
            >
              <div
                style={{
                  background: "#f8f9fa",
                  boxShadow: "none",
                  border: "none",
                }}
                className={`${styles["panel-heading"]} ${styles["panel-title"]}`}
              >
                <h3>Property Notice To Quit</h3>
              </div>

              <div
                className={styles["panel-body"]}
                style={{ background: "#f8f9fa", boxShadow: "none" }}
              >
                {/* Debug Info
                {process.env.NODE_ENV === 'development' && (
                  <div style={{ 
                    background: '#f0f0f0', 
                    padding: '10px', 
                    marginBottom: '10px', 
                    fontSize: '12px',
                    border: '1px solid #ddd'
                  }}>
                    Debug: {noticeToQuits.length} items loaded, API Failed: {apiFailed.toString()}, All Data: {allData.length}
                  </div>
                )} */}

                {/* Table Controls */}
                <div className={styles["table-controls"]}>
                  <div className={styles["show-entries"]}>
                    <span>Show</span>
                    <select
                      className={styles["entries-select"]}
                      onChange={handleEntriesChange}
                      defaultValue="30"
                    >
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="30">30</option>
                      <option value="50">50</option>
                      <option value="100">100</option>
                    </select>
                    <span>entries</span>
                  </div>
                  <div className={styles["search-box"]}>
                    <span>Search:</span>
                    <input
                      type="text"
                      className={styles["search-input"]}
                      placeholder="Search..."
                      onChange={handleSearch}
                    />
                  </div>
                  <div className={styles["action-buttons"]}>
                    <button
                      className={styles["btn-export"]}
                      onClick={handleExportCSV}
                    >
                      Export as CSV
                    </button>
                    <button
                      className={styles["btn-export"]}
                      onClick={handleExportPDF}
                    >
                      Export as PDF
                    </button>
                    <button
                      className={styles["btn-export"]}
                      onClick={handlePrintTable}
                    >
                      Print table
                    </button>
                    <div className={styles["column-visibility-container"]}>
                      <button
                        className={styles["btn-export"]}
                        onClick={handleColumnVisibility}
                      >
                        Column visibility
                      </button>
                      {showColumnVisibility && (
                        <div className={styles["column-visibility-dropdown"]}>
                          <label className={styles["column-checkbox"]}>
                            <input
                              type="checkbox"
                              checked={visibleColumns.creator}
                              onChange={() => toggleColumn("creator")}
                            />
                            Creator
                          </label>
                          <label className={styles["column-checkbox"]}>
                            <input
                              type="checkbox"
                              checked={visibleColumns.property}
                              onChange={() => toggleColumn("property")}
                            />
                            Property
                          </label>
                          <label className={styles["column-checkbox"]}>
                            <input
                              type="checkbox"
                              checked={visibleColumns.noticeDate}
                              onChange={() => toggleColumn("noticeDate")}
                            />
                            Notice date
                          </label>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {noticeToQuits.length > 0 ? (
                  <div className={"users-table-wrapper"}>
                    <table
                      className={"users-table"}
                      style={{ background: "#f8f9fa", boxShadow: "none" }}
                    >
                      <thead>
                        <tr>
                          {visibleColumns.creator && <th>Creator</th>}
                          {visibleColumns.property && (
                            <th className={styles["sortable"]}>
                              Property
                              <span className={styles["sort-icon"]}>↕</span>
                            </th>
                          )}
                          {visibleColumns.noticeDate && (
                            <th className={styles["sortable"]}>
                              Notice date
                              <span className={styles["sort-icon"]}>↕</span>
                              <span className={styles["filter-icon"]}>🔍</span>
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {noticeToQuits.map((noticeToQuit) => (
                          <tr
                            key={noticeToQuit.id}
                            className={styles["table-row"]}
                            onClick={() => handleRowClick(noticeToQuit)}
                          >
                            {visibleColumns.creator && (
                              <td>
                                <div className={styles["creator-info"]}>
                                  <div className={styles["creator-name"]}>
                                    {noticeToQuit?.creator_name || "Unknown"}
                                  </div>
                                </div>
                              </td>
                            )}
                            {visibleColumns.property && (
                              <td>
                                <div className={styles["property-info"]}>
                                  <a
                                    href={`/properties/${noticeToQuit.property_id || noticeToQuit.property?.id}`}
                                    className={styles["property-link"]}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      const propertyId = noticeToQuit.property_id || noticeToQuit.property?.id;
                                      if (propertyId) {
                                        navigate(`/properties/${propertyId}`);
                                      }
                                    }}
                                  >
                                    {noticeToQuit?.property_name ||
                                      "Unknown Property"}
                                  </a>
                                </div>
                              </td>
                            )}
                            {visibleColumns.noticeDate && (
                              <td>
                                <div className={styles["notice-date"]}>
                                  {formatDate(noticeToQuit.date_notice)}
                                </div>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className={styles["no-data"]}>
                    No property notice to quit!
                  </div>
                )}

                {/* Pagination */}
                {allData.length > 0 && (
                  <div className={styles["pagination-container"]}>
                    <div className={styles["pagination-info"]}>
                      Showing {getPaginationInfo().startEntry} to{" "}
                      {getPaginationInfo().endEntry} of{" "}
                      {getPaginationInfo().totalEntries} entries
                    </div>
                    <div className={styles["pagination-buttons"]}>
                      {getPaginationButtons().map((button, index) => (
                        <button
                          key={index}
                          className={`${styles["pagination-button"]} ${
                            button.type === "number" && button.active
                              ? styles["pagination-button-active"]
                              : ""
                          } ${
                            button.disabled
                              ? styles["pagination-button-disabled"]
                              : ""
                          }`}
                          onClick={() => {
                            if (
                              !button.disabled &&
                              button.type !== "ellipsis"
                            ) {
                              handlePageChange(button.page);
                            }
                          }}
                          disabled={button.disabled}
                        >
                          {button.type === "previous" && "Previous"}
                          {button.type === "next" && "Next"}
                          {button.type === "number" && button.page}
                          {button.type === "ellipsis" && "..."}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyNoticeToQuitIndex;
